import React, { useEffect, useState } from "react";
import "./HorizontalScrollCalendar.css";
import { Box, Typography } from "@mui/material";
import { API, graphqlOperation } from "aws-amplify";
import { listDailyAnsweredQuestions } from "../graphql/queries";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const intensityFor = count => {
  if (!count) return 0;
  if (count >= 5) return 3;
  if (count >= 3) return 2;
  return 1;
};

const HorizontalScrollCalendar = ({ month, year, userId }) => {
  const [completedActions, setCompletedActions] = useState({});
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const today = new Date();
  const isCurrentMonth =
    today.getMonth() === month - 1 && today.getFullYear() === year;
  const todayDay = today.getDate();

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const response = await API.graphql(
          graphqlOperation(listDailyAnsweredQuestions, {
            filter: { userID: { eq: userId } }
          })
        );
        if (cancelled) return;
        const items = response.data.listDailyAnsweredQuestions.items || [];
        const map = {};
        items.forEach(item => {
          const parts = String(item.date).split("-");
          if (parts.length !== 3) return;
          const y = Number(parts[0]);
          const m = Number(parts[1]);
          const day = Number(parts[2]);
          if (m === month && y === year) {
            map[day] = (map[day] || 0) + (item.answeredQuestionsCount || 0);
          }
        });
        setCompletedActions(map);
      } catch (error) {
        console.error("Error fetching daily answered questions:", error);
      }
    };
    if (userId) fetchData();
    return () => {
      cancelled = true;
    };
  }, [userId, month, year]);

  const totalThisMonth = Object.values(completedActions).reduce(
    (a, b) => a + (b || 0),
    0
  );
  const activeDays = Object.keys(completedActions).length;

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) {
    cells.push({ blank: true, key: `blank-${i}` });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const count = completedActions[day] || 0;
    cells.push({
      blank: false,
      key: `day-${day}`,
      day,
      count,
      intensity: intensityFor(count),
      isToday: isCurrentMonth && day === todayDay
    });
  }

  return (
    <Box
      sx={{
        backgroundColor: SURFACE,
        borderRadius: "24px",
        border: `1px solid ${INK_HAIRLINE}`,
        boxShadow:
          "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
        p: { xs: 3, sm: 4 },
        textAlign: "left"
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
          flexWrap: "wrap"
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.7rem",
              fontWeight: 500,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: INK_FAINT,
              mb: 0.75
            }}
          >
            Activity · {MONTHS[month - 1]} {year}
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "1.05rem",
              fontWeight: 500,
              color: INK,
              lineHeight: 1.35,
              maxWidth: "44ch"
            }}
          >
            {totalThisMonth > 0
              ? `${totalThisMonth} answer${totalThisMonth === 1 ? "" : "s"} across ${activeDays} day${activeDays === 1 ? "" : "s"} this month.`
              : "No answers logged this month yet — record one to start a streak."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.66rem",
              letterSpacing: "0.14em",
              color: INK_FAINT,
              textTransform: "uppercase"
            }}
          >
            Less
          </Typography>
          {[0, 1, 2, 3].map(level => (
            <Box
              key={level}
              sx={{
                width: 12,
                height: 12,
                borderRadius: "3px",
                backgroundColor:
                  level === 0
                    ? "rgba(15, 17, 33, 0.04)"
                    : level === 1
                    ? "rgba(56, 84, 232, 0.12)"
                    : level === 2
                    ? "rgba(56, 84, 232, 0.32)"
                    : "rgba(56, 84, 232, 0.6)",
                border: `1px solid ${
                  level === 0
                    ? "rgba(15, 17, 33, 0.06)"
                    : "rgba(56, 84, 232, 0.18)"
                }`
              }}
            />
          ))}
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.66rem",
              letterSpacing: "0.14em",
              color: INK_FAINT,
              textTransform: "uppercase"
            }}
          >
            More
          </Typography>
        </Box>
      </Box>

      <div className="month-grid-headers">
        {WEEKDAYS.map((d, i) => (
          <div key={`h-${i}`} className="weekday-header">
            {d}
          </div>
        ))}
      </div>

      <div className="month-grid">
        {cells.map(c =>
          c.blank ? (
            <div key={c.key} className="month-cell is-blank" />
          ) : (
            <div
              key={c.key}
              className={`month-cell intensity-${c.intensity} ${
                c.isToday ? "is-today" : ""
              }`}
              title={`${MONTHS[month - 1]} ${c.day}: ${c.count} answer${c.count === 1 ? "" : "s"}`}
            >
              <span className="month-cell-day">{c.day}</span>
              {c.count > 0 && (
                <span className="month-cell-count">{c.count}</span>
              )}
            </div>
          )
        )}
      </div>
    </Box>
  );
};

export default HorizontalScrollCalendar;
